import * as React from 'react';

import {Checkbox} from 'shells/checkbox/checkbox';
import {Stack} from 'shells/layout/stack/stack';
import {Popover} from 'shells/popover/popover';
import {PopoverForm} from 'shells/popover-form/popover-form';
import {TwoLine} from 'shells/two-line/two-line';
import {Body} from 'shells/typography/body';

import type {PotentialActivityEntityFragment as Participant} from 'nutshell-graphql-types';

import {getExternalAttendeesFromParticipants} from './helpers';
import type {ParticipantWithCheckedValue} from './types';

import styles from './bulk-create-contacts-from-attendees-popover.module.css';

type Props = {
    anchor: HTMLElement | null | undefined;
    handleLogActivity: (externalAttendeeIds: Array<string>) => void;
    participants: Array<Participant>;
    onClose: () => void;
};

export function BulkCreateContactsFromAttendeesPopover(props: Props) {
    const {handleLogActivity, onClose} = props;
    const [attendeesMapped, setAttendeesMapped] = React.useState<
        Array<ParticipantWithCheckedValue>
    >(getExternalAttendeesFromParticipants(props.participants));

    const handleLogActivityAndCreateContacts = React.useCallback(() => {
        onClose();
        handleLogActivity(
            attendeesMapped.filter((attendee) => attendee.isChecked).map((attendee) => attendee.id)
        );
    }, [attendeesMapped, handleLogActivity, onClose]);

    const handleCheckboxChange = React.useCallback(
        (externalAttendeeId: string) => {
            setAttendeesMapped(
                attendeesMapped.map((attendee) => {
                    if (attendee.id === externalAttendeeId) {
                        return {...attendee, isChecked: !attendee.isChecked};
                    }

                    return attendee;
                })
            );
        },
        [attendeesMapped]
    );

    const hasAtLeastOneChecked = React.useMemo(() => {
        return attendeesMapped.some((attendee) => attendee.isChecked);
    }, [attendeesMapped]);

    return (
        <Popover anchor={props.anchor}>
            <PopoverForm
                width={300}
                onClose={onClose}
                onSubmit={handleLogActivityAndCreateContacts}
                title='Select people to create'
                allowTitleWrapping={true}
                saveButtonText='Create and log'
                saveButtonDisabled={!hasAtLeastOneChecked}
                saveButtonDisabledTooltip='Select at least one person to log this activity'
            >
                <div className={styles['bulk-create-contacts-from-attendees-popover']}>
                    <Stack spacing={16}>
                        {attendeesMapped.map((attendee) => (
                            <Checkbox
                                key={attendee.id}
                                isLarge={true}
                                onChange={() => handleCheckboxChange(attendee.id)}
                                checked={attendee.isChecked}
                                label={
                                    <TwoLine
                                        topRow={
                                            <Body isBold={true}>
                                                {attendee.name ? attendee.name : 'Unnamed person'}
                                            </Body>
                                        }
                                        bottomRow={
                                            <Body size='small' color='light'>
                                                {attendee.primaryEmailAddress ||
                                                    attendee.primaryPhoneNumber}
                                            </Body>
                                        }
                                        shouldWrap={true}
                                        shouldCenterSingleLine={true}
                                    />
                                }
                            />
                        ))}
                    </Stack>
                </div>
            </PopoverForm>
        </Popover>
    );
}
